import React, { useState, useEffect, useRef } from 'react';
import { QuoteItemPicker } from './QuoteItemPicker';
import { nsConnectMessage } from './NetSuiteConnect';
import { buildQuoteDiff } from '../quoteDiff';
import './QuoteBuilder.css';
import { api } from '../api';
import DatasheetPreview from './DatasheetPreview';
import USDatasheetPreview from './USDatasheetPreview';
import { useRegion } from '../region';
import { matchSupplier, DEFAULT_SUPPLIER, EXCHANGE_RATES, SUPPLIERS, SUPPLIER_FREIGHT, DEFAULT_FREIGHT, MARKUP_TIERS, EU_TRADE_DISCOUNTS, calcRow, toDisplay } from '../pricingData';
export { buildQuoteDiff } from '../quoteDiff';

const CURRENCIES = Object.keys(EXCHANGE_RATES);

// Quote pricing choices: Wholesale +5% is the default tier.
export const TIER_CHOICES = [
  ...MARKUP_TIERS.map(t => ({ key: t.key, label: t.label })),
  { key: 'custom', label: 'Custom' },
];
export const tierLabel = k => (TIER_CHOICES.find(t => t.key === k) || {}).label || k;

function newRow() {
  return { id: crypto.randomUUID(), desc: '', cost: '', originCur: 'USD', supplier: DEFAULT_SUPPLIER, freightType: 'sea', euDiscount: '', customFreight: '', nsVendor: '' };
}

function newLine(rowIdx, extra = {}) {
  return { rowIdx, tier: 'wholesale5', qty: 1, custom: '', code: '', displayName: '', desc: '', lineRef: '', notes: '', nsItemId: null, nsFound: false, dsId: null, dsName: '', dsChecked: false, ...extra };
}

// A text-only line: no item, no qty, no price. Pushes to NetSuite as a real
// Description line (built-in item -3), the same as adding one in NetSuite.
function newNote(extra = {}) {
  return { kind: 'note', noteId: crypto.randomUUID(), rowIdx: null, text: '', qty: 0, tier: 'custom', custom: '', ...extra };
}
const isNote = l => l.kind === 'note';
const lineKey = l => (isNote(l) ? l.noteId : l.rowIdx);

function NsPricePanel({ row, result, toast, onClose }) {
  const [q, setQ] = useState(row.desc || '');
  const [hits, setHits] = useState(null);
  const [item, setItem] = useState(null); // {id, itemid, vendor info}
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const newPrice = result ? Number(result.net.toFixed(2)) : null;

  async function search() {
    if (!q.trim()) return;
    setBusy(true);
    try { const { items } = await api.nsSearch(q.trim()); setHits(items || []); }
    catch (e) { toast('Search failed: ' + (e.response?.data?.detail || e.message), true); }
    setBusy(false);
  }
  async function pick(hit) {
    setBusy(true);
    try {
      const info = await api.nsItemFields(hit.id);
      setItem({ id: hit.id, code: hit.itemid, vendor: info.vendor || {} });
      setHits(null);
    } catch (e) { toast('Could not load item: ' + (e.response?.data?.detail || e.message), true); }
    setBusy(false);
  }
  async function push() {
    setBusy(true);
    try {
      const res = await api.nsUpdatePricing({ item_id: item.id, purchase_price: newPrice, item_name: item.code });
      setDone(res);
      toast(`Purchase price updated: ${res.old_price ?? '—'} → ${res.new_price}`);
    } catch (e) { toast('Update failed: ' + (e.response?.data?.detail || e.message), true); }
    setBusy(false);
  }

  return (
    <div className="pc-ns" data-testid="pc-ns-panel">
      <div className="pc-ns-head">
        <strong>Update NetSuite pricing</strong>
        <button className="btn btn-sm" data-testid="pc-ns-close" onClick={onClose}>✕</button>
      </div>
      {!item && (
        <>
          <div className="nsp-search-row">
            <input className="input" data-testid="pc-ns-search" placeholder="Paste / search NetSuite item code…" value={q}
              onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') search(); }} />
            <button className="btn btn-sm" onClick={search} disabled={busy}>{busy ? '…' : 'Search'}</button>
          </div>
          {hits && (
            <div className="nsp-search-results">
              {hits.length === 0 && <div className="empty-note">No items found.</div>}
              {hits.map(h => (
                <button key={h.id} className="nsp-search-hit" data-testid={'pc-ns-hit-' + h.id} onClick={() => pick(h)}>
                  <strong>{h.itemid}{h.isinactive === 'T' ? ' · INACTIVE' : ''}</strong><span>{h.displayname}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
      {item && (
        <div className="pc-ns-detail" data-testid="pc-ns-detail">
          <div><strong>{item.code}</strong></div>
          <div>Vendor: {item.vendor.vendor_name || '— none —'}</div>
          <div>Current purchase price: <strong>{item.vendor.purchase_price ?? '—'}</strong></div>
          <div>New purchase price (net cost, {row.originCur}): <strong>{newPrice ?? '—'}</strong></div>
          {done ? (
            <a className="btn btn-sm" href={done.url} target="_blank" rel="noreferrer">Updated ✓ — open in NetSuite ↗</a>
          ) : (
            <div className="pc-ns-actions">
              <button className="btn btn-sm" onClick={() => { setItem(null); setDone(null); }}>Change item</button>
              <button className="btn btn-primary btn-sm" data-testid="pc-ns-update" disabled={busy || newPrice == null || !item.vendor.vendor_id} onClick={push}>
                {busy ? 'Updating…' : 'Update purchase price'}
              </button>
            </div>
          )}
          {!item.vendor.vendor_id && <div className="nsp-hint">This item has no vendor line — add a vendor in NetSuite first.</div>}
        </div>
      )}
    </div>
  );
}

export default function PricingCalc({ canPriceUpdate, toast, initialQuote, initialCart }) {
  const [displayCur, setDisplayCur] = useState('AUD');
  const [rows, setRows] = useState([newRow()]);
  const [nsOpen, setNsOpen] = useState(null); // row index
  const [opportunity, setOpportunity] = useState('');
  const [quoteLines, setQuoteLines] = useState([]);
  const [quoteId, setQuoteId] = useState(null); // editing an existing saved quote
  const [saving, setSaving] = useState(false);
  const [addCode, setAddCode] = useState('');
  const [adding, setAdding] = useState(false);
  const [addStatus, setAddStatus] = useState('');
  const [nsHits, setNsHits] = useState(null); // multiple NS matches to pick from
  const [findState, setFindState] = useState(null); // {idx, hits, busy, rect} — pull cost from NS on a pricing row
  const [zoom, setZoom] = useState(() => {
    const z = Number(localStorage.getItem('pc_zoom'));
    return z >= 60 && z <= 120 ? z : 100;
  });
  useEffect(() => { localStorage.setItem('pc_zoom', String(zoom)); }, [zoom]);
  const [loadRef, setLoadRef] = useState('');
  const [oppInfo, setOppInfo] = useState({ status: 'idle' }); // idle|checking|found|missing|error
  const [quoteDate, setQuoteDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [salesReps, setSalesReps] = useState(null); // null=loading, []=unavailable
  const [repsErr, setRepsErr] = useState('');
  const [salesRep, setSalesRep] = useState('');
  const [nsQuote, setNsQuote] = useState(null); // {quote_number, url}
  const [creatingNs, setCreatingNs] = useState(false);
  const [oppQuotes, setOppQuotes] = useState([]); // existing NS quotes on the opportunity
  const [nsTarget, setNsTarget] = useState('new'); // 'new' | NS estimate id to update
  const [previewQ, setPreviewQ] = useState(null); // {tranid, ...} + lines once loaded
  const [dsPick, setDsPick] = useState(null); // {rowIdx, matches}
  const [expandedDesc, setExpandedDesc] = useState({}); // rowIdx -> true
  const [compact, setCompact] = useState(() => localStorage.getItem('pc_compact') === '1');
  useEffect(() => { localStorage.setItem('pc_compact', compact ? '1' : '0'); }, [compact]);
  const [dsFind, setDsFind] = useState(null); // {rowIdx, code} — attach a datasheet to one line
  const [dsBannerOff, setDsBannerOff] = useState(false);
  const [dsBannerOpen, setDsBannerOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);
  const [quoteDiff, setQuoteDiff] = useState(null); // {target, added, removed, changed, same} | {target, loading}
  const [editingItem, setEditingItem] = useState(null);
  const [nsError, setNsError] = useState(null);
  const dsRequests = useRef({});
  useEffect(() => { setNsError(null); }, [quoteLines, opportunity]);

  useEffect(() => {
    api.nsSalesReps().then(d => setSalesReps(d.items || []))
      .catch(e => { setSalesReps([]); setRepsErr(e.response?.data?.detail || e.message); });
  }, []);

  async function checkOpportunity(num) {
    const n = (num || '').trim();
    setOppQuotes([]); setNsTarget('new');
    if (!n) { setOppInfo({ status: 'idle' }); return; }
    setOppInfo({ status: 'checking' });
    try {
      const data = await api.nsOpportunity(n);
      setOppInfo({ status: 'found', data });
      api.nsOppQuotes(n).then(d => setOppQuotes(d.items || [])).catch(() => setOppQuotes([]));
    } catch (e) {
      if (e.response?.status === 404) setOppInfo({ status: 'missing' });
      else setOppInfo({ status: 'error', detail: e.response?.data?.detail || e.message });
    }
  }

  useEffect(() => { if (initialQuote && initialQuote.q) hydrate(initialQuote.q); }, [initialQuote]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (initialCart && initialCart.items && initialCart.items.length) hydrateFromCart(initialCart); }, [initialCart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefill from the Product Search quote cart: rows + quote lines + opportunity,
  // then enrich costs/datasheets from NetSuite in the background.
  function hydrateFromCart(req) {
    dsRequests.current = {}; setEditingItem(null); setExpandedDesc({});
    const items = req.items || [];
    setQuoteId(null); setNsQuote(null); setNsOpen(null);
    setRows(items.map(it => ({ ...newRow(), desc: it.code || it.name || '' })));
    setQuoteLines(items.map((it, i) => newLine(i, {
      code: it.code || '', displayName: it.name || it.code || '', desc: it.desc || '',
      qty: Number(it.qty) || 1, nsItemId: it.nsItemId || null, nsFound: !!it.nsItemId,
      dsId: it.dsId || null, dsName: it.dsName || '', dsChecked: !!it.dsId,
    })));
    setOpportunity(req.opportunity || '');
    if (req.opportunity) checkOpportunity(req.opportunity);
    items.forEach((it, i) => {
      if (!it.dsId) checkDatasheet(i, it.code, it.name, it.nsItemId);
      if (it.nsItemId) {
        api.nsItemFields(it.nsItemId).then(info => {
          const pp = info.vendor && info.vendor.purchase_price;
          const vendor = (info.vendor && info.vendor.vendor_name) || '';
          const sup = matchSupplier(vendor);
          update(i, {
            nsVendor: vendor,
            ...(pp != null && pp !== '' ? { cost: String(pp) } : {}),
            ...(sup ? { supplier: sup } : {}),
          });
        }).catch(() => {});
      }
    });
    toast(items.length + ' item' + (items.length > 1 ? 's' : '') + ' added from Product Search — set costs & tiers, then save the quote');
  }

  function hydrate(q) {
    dsRequests.current = {}; setEditingItem(null); setExpandedDesc({});
    const newRows = [];
    const lines = [];
    const toCheck = [];
    q.lines.forEach(l => {
      if (l.kind === 'note') { lines.push(newNote({ text: l.desc || '' })); return; }
      const i = newRows.length;
      newRows.push({ ...newRow(), ...(l.pricing || {}), id: crypto.randomUUID() });
      lines.push({
        rowIdx: i, tier: l.tier, qty: l.qty, custom: l.tier === 'custom' ? String(l.unit) : '',
        code: l.code || '',
        displayName: l.display_name || l.desc || '',
        desc: l.display_name ? (l.desc || '') : '',
        lineRef: l.line_ref || '', notes: l.notes || '',
        nsItemId: l.ns_item_id || null, nsFound: !!l.ns_found,
        nsInactive: !!l.ns_inactive,
        dsId: l.datasheet_id || null, dsName: l.ds_name || '',
        dsSource: l.datasheet_id ? 'library' : (l.archive_id ? 'archive' : null),
        dsArchiveId: l.archive_id || null,
        dsChecked: !!(l.datasheet_id || l.archive_id),
      });
      if (!l.datasheet_id && !l.archive_id) toCheck.push([i, l]);
    });
    setRows(newRows.length ? newRows : [newRow()]);
    setQuoteLines(lines);
    // Anything still unlinked gets re-checked automatically — a datasheet may
    // have been added to the library or the archive since the quote was saved.
    toCheck.forEach(([i, l]) => checkDatasheet(i, l.code, l.display_name || l.desc, l.ns_item_id));
    setOpportunity(q.opportunity);
    setDisplayCur(q.display_cur || 'AUD');
    setQuoteId(q.id);
    setNsQuote(q.ns_quote_number || q.ns_quote_url ? { quote_number: q.ns_quote_number, url: q.ns_quote_url } : null);
    setNsOpen(null);
    checkOpportunity(q.opportunity);
  }

  function update(i, patch) {
    if (patch.cost !== undefined) setQuoteLines(ls => ls.map(l => l.rowIdx === i ? { ...l, costLoading: false } : l));
    setRows(rs => rs.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, ...patch };
      if (patch.supplier !== undefined) {
        const auto = EU_TRADE_DISCOUNTS[patch.supplier];
        next.euDiscount = auto !== undefined ? String(auto) : '';
      }
      return next;
    }));
  }

  const fmt = (aud) => aud == null ? '—' : toDisplay(aud, displayCur).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtN = n => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const inQuote = i => quoteLines.some(l => l.rowIdx === i);
  const toggleQuote = i => {
    if (inQuote(i)) { setQuoteLines(ls => ls.filter(l => l.rowIdx !== i)); return; }
    const desc = (rows[i] || {}).desc || '';
    setQuoteLines(ls => [...ls, newLine(i, { code: desc, displayName: desc })]);
    checkDatasheet(i, desc, '');
    // Best-effort NetSuite enrich in the background: real item + description auto-fill.
    if (desc.trim()) {
      api.nsSearch(desc.trim()).then(({ items }) => {
        const hit = (items || []).find(h => (h.itemid || '').toLowerCase() === desc.trim().toLowerCase()) || ((items || []).length === 1 ? items[0] : null);
        if (hit) setQuoteLines(ls => ls.map(l => l.rowIdx === i ? { ...l, code: hit.itemid, displayName: hit.displayname || hit.itemid, desc: l.desc || hit.description || '', nsItemId: hit.id, nsFound: true, nsInactive: hit.isinactive === 'T' } : l));
        if (hit && hit.isinactive === 'T') toast('"' + hit.itemid + '" is INACTIVE in NetSuite — the quote push will be rejected until it is reactivated or replaced.', true);
      }).catch(() => {});
    }
  };
  const setLine = (rowIdx, patch) => setQuoteLines(ls => ls.map(l => l.rowIdx === rowIdx ? { ...l, ...patch } : l));
  const removeRow = i => {
    dsRequests.current = {}; setEditingItem(null); setExpandedDesc({});
    setRows(rs => rs.filter((_, idx) => idx !== i));
    setQuoteLines(ls => ls.filter(l => l.rowIdx !== i).map(l => l.rowIdx > i ? { ...l, rowIdx: l.rowIdx - 1 } : l));
    setNsOpen(null);
  };
  const moveLine = (index, direction) => setQuoteLines(lines => {
    const target = index + direction;
    if (target < 0 || target >= lines.length) return lines;
    const next = [...lines];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const dropLine = (from, to) => setQuoteLines(lines => {
    if (from === to || from == null || to == null) return lines;
    const next = [...lines];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  });
  // Copy a line (same pricing inputs, tier, qty, description) directly beneath it —
  // for quoting the same item in another finish. The line ref is left blank.
  function duplicateLine(index) {
    const src = quoteLines[index];
    if (!src) return;
    const pricing = { ...(rows[src.rowIdx] || newRow()), id: crypto.randomUUID() };
    const copyIdx = rows.length;
    setRows(rs => [...rs, pricing]);
    setQuoteLines(ls => {
      const next = [...ls];
      next.splice(index + 1, 0, { ...src, rowIdx: copyIdx, pricingId: pricing.id, lineRef: '' });
      return next;
    });
    toast('Line copied — edit the code, finish and description on the copy.');
  }
  function replaceItem(item) {
    const rowIdx = editingItem.rowIdx;
    const pricing = { ...newRow(), desc: item.code, cost: item.cost };
    update(rowIdx, pricing);
    setLine(rowIdx, { ...item, pricingId: pricing.id, costLoading: false, dsId: null, dsName: '', dsSource: null, dsDup: false, dsMatches: [], dsChecked: false });
    checkDatasheet(rowIdx, item.code, item.displayName, item.nsItemId);
    setEditingItem(null);
    toast('Item replaced — quantity, pricing tier, line reference and notes retained. Check the new cost before saving.');
  }

  // ---- add-by-code (NetSuite lookup) ----
  async function addByCode() {
    const code = addCode.trim();
    if (!code) return;
    setAdding(true);
    setAddStatus('Searching NetSuite for ' + code + '…');
    let hits = [];
    try { const r = await api.nsSearch(code); hits = r.items || []; }
    catch (e) { toast('NetSuite search failed: ' + (e.response?.data?.detail || e.message), true); setAddStatus('Search failed — no item added.'); setAdding(false); return; }
    if (hits.length) {
      const exact = hits.find(h => (h.itemid || '').toLowerCase() === code.toLowerCase());
      if (exact) await pickNsItem(exact);
      else if (hits.length === 1) await pickNsItem(hits[0]);
      else { setNsHits(hits); setAddStatus(hits.length + ' matching items found.'); }
    } else addLine({ code, displayName: code, desc: '', nsItemId: null, nsFound: false, cost: '' });
    setAdding(false);
  }
  async function pickNsItem(hit) {
    const pricingId = addLine({ code: hit.itemid, displayName: hit.displayname || hit.itemid, desc: hit.description || hit.salesdescription || '', nsItemId: hit.id, nsFound: true, nsInactive: hit.isinactive === 'T', cost: '', costLoading: true });
    if (hit.isinactive === 'T') toast('"' + hit.itemid + '" is INACTIVE in NetSuite — the quote push will be rejected until it is reactivated or replaced.', true);
    setNsHits(null);
    // Item identity/description is ready now. Vendor lookup must not hold up
    // the line or overwrite a manually entered cost or replacement item.
    api.nsItemFields(hit.id).then(info => {
      const price = info.vendor?.purchase_price;
      const vendor = info.vendor?.vendor_name || '';
      const sup = matchSupplier(vendor);
      // Supplier comes from the item's vendor line in NetSuite; only fill it
      // while the row is still on the default (never overwrite a manual pick).
      setRows(rs => rs.map(r => {
        if (r.id !== pricingId) return r;
        const next = { ...r, nsVendor: vendor };
        if (price != null && price !== '' && r.cost === '') next.cost = String(price);
        if (sup && r.supplier === DEFAULT_SUPPLIER) {
          next.supplier = sup;
          next.euDiscount = EU_TRADE_DISCOUNTS[sup] !== undefined ? String(EU_TRADE_DISCOUNTS[sup]) : '';
        }
        return next;
      }));
      if (vendor && !sup) toast('NetSuite vendor "' + vendor + '" for ' + hit.itemid + ' is not in the supplier list — pick the supplier manually.', true);
      if (price == null || price === '') toast('No purchase cost for ' + hit.itemid + ' — enter a cost or custom price.', true);
    }).catch(() => toast('Could not load cost for ' + hit.itemid + ' — enter a cost or custom price.', true))
      .finally(() => setQuoteLines(ls => ls.map(l => l.pricingId === pricingId ? { ...l, costLoading: false } : l)));
  }
  function addLine({ code, displayName, desc, nsItemId, nsFound, nsInactive = false, cost, costLoading = false }) {
    const idx = rows.length;
    const pricing = { ...newRow(), desc: code, cost };
    setRows(rs => [...rs, pricing]);
    setQuoteLines(ls => [...ls, newLine(idx, { code, displayName: displayName || '', desc: desc || '', nsItemId, nsFound, nsInactive, pricingId: pricing.id, costLoading })]);
    checkDatasheet(idx, code, displayName, nsItemId);
    setAddCode('');
    setAddStatus('Added ' + code + '.');
    return pricing.id;
  }
  async function checkDatasheet(rowIdx, code, name, nsItemId) {
    const request = {};
    dsRequests.current[rowIdx] = request;
    let res = null;
    try {
      res = await api.dsCheck({ code, name: name === 'Generic Budgetary Item' ? '' : name, ns_item_id: nsItemId || null });
    } catch (e) { /* leave unmatched */ }
    if (dsRequests.current[rowIdx] !== request) return;
    const best = res && res.best;
    const archive = res ? (res.matches || []).find(m => m.source === 'archive') : null;
    setQuoteLines(ls => ls.map(l => l.rowIdx === rowIdx ? {
      ...l,
      dsId: best ? best.id : null,
      dsName: best ? best.name : (archive ? archive.name : ''),
      dsSource: best ? best.source : (archive ? 'archive' : null),
      dsArchiveId: archive ? archive.id : null,
      dsDup: !!(res && res.duplicate),
      dsMatches: res ? (res.matches || []) : [],
      dsChecked: true,
    } : l));
  }

  const unitFor = line => {
    if (isNote(line)) return 0;
    if (line.tier === 'custom') return Number(line.custom) || 0;
    const res = calcRow(rows[line.rowIdx] || {});
    if (!res) return 0;
    return toDisplay(res.tiers[line.tier] || 0, displayCur);
  };
  const quoteTotal = quoteLines.reduce((a, l) => a + unitFor(l) * (Number(l.qty) || 0), 0);
  const itemLines = quoteLines.filter(l => !isNote(l));
  const noteCount = quoteLines.length - itemLines.length;

  const addNote = () => {
    setQuoteLines(ls => [...ls, newNote()]);
    setAddStatus('Description line added — type the text, then drag it where you want it.');
  };
  const setNoteText = (noteId, text) => setQuoteLines(ls => ls.map(l => (l.noteId === noteId ? { ...l, text } : l)));

  async function saveQuote() {
    if (quoteLines.some(l => l.costLoading && l.tier !== 'custom')) { toast('Costs are still loading — wait or enter your cost/custom price.', true); return null; }
    if (!opportunity.trim()) { toast('Enter an opportunity number first', true); return; }
    if (!itemLines.length) { toast('Add at least one item to the quote', true); return; }
    setSaving(true);
    try {
      const body = {
        opportunity: opportunity.trim(),
        display_cur: displayCur,
        lines: quoteLines.filter(l => !isNote(l) || l.text.trim()).map(l => (isNote(l) ? {
          kind: 'note',
          desc: l.text.trim(),
          display_name: '', line_ref: '', code: '', notes: '',
          tier: 'custom', qty: 0, unit: 0, line_total: 0,
          ns_item_id: null, ns_found: false, ns_inactive: false,
          datasheet_id: null, ds_name: '', archive_id: null, pricing: null,
        } : {
          kind: 'item',
          desc: l.desc || '',
          display_name: l.displayName || (l.nsFound ? '' : (l.code || '')),
          line_ref: l.lineRef || '',
          code: l.code || '',
          notes: l.notes || '',
          tier: l.tier,
          qty: Number(l.qty) || 0,
          unit: Number(unitFor(l).toFixed(2)),
          line_total: Number((unitFor(l) * (Number(l.qty) || 0)).toFixed(2)),
          ns_item_id: l.nsItemId,
          ns_found: !!l.nsFound,
          ns_inactive: !!l.nsInactive,
          datasheet_id: l.dsId,
          ds_name: l.dsName || '',
          archive_id: l.dsSource === 'archive' ? (l.dsArchiveId || null) : null,
          pricing: { ...(rows[l.rowIdx] || newRow()) },
        })),
        total: Number(quoteTotal.toFixed(2)),
      };
      const q = quoteId ? await api.updateQuote(quoteId, body) : await api.saveQuote(body);
      setQuoteId(q.id);
      const nds = body.lines.filter(l => l.datasheet_id).length;
      toast(`Quote ${quoteId ? 'updated' : 'saved'} for ${q.opportunity}` + (nds ? ` — ${nds} datasheet(s) synced to project "${q.opportunity}"` : ''));
      setSaving(false);
      return q;
    } catch (e) { toast('Save failed: ' + (e.response?.data?.detail || e.message), true); }
    setSaving(false);
    return null;
  }

  async function createInNetSuite(confirmed = false) {
    setNsError(null);
    if (!nsQuote && oppInfo.status !== 'found') { toast('Enter a valid opportunity number first — it must exist in NetSuite', true); return; }
    if (!quoteDate) { toast('Pick the quote date first', true); return; }
    // Updating an existing quote? Show what will change first.
    if (nsTarget !== 'new' && !confirmed) {
      const target = oppQuotes.find(x => x.id === nsTarget) || { id: nsTarget, tranid: 'quote' };
      setQuoteDiff({ target, loading: true });
      try {
        const { items } = await api.nsEstimateLines(nsTarget);
        setQuoteDiff({ target, ...buildQuoteDiff((items || []).filter(x => !x.is_description), itemLines.map(l => ({
          key: (l.nsFound ? l.code : (l.displayName || l.code)) || '(no code)',
          qty: Number(l.qty) || 0, unit: unitFor(l) || 0,
          desc: l.desc, displayName: l.displayName, lineRef: l.lineRef, notes: l.notes,
        }))) });
      } catch (e) {
        setQuoteDiff(null);
        toast('Could not load ' + target.tranid + ' from NetSuite for comparison: ' + (e.response?.data?.detail || e.message), true);
      }
      return;
    }
    setQuoteDiff(null);
    setCreatingNs(true);
    try {
      let id = quoteId;
      if (!id) { const q = await saveQuote(); if (!q) { setCreatingNs(false); return; } id = q.id; }
      else { const q = await saveQuote(); if (!q) { setCreatingNs(false); return; } } // push latest edits first
      const res = await api.nsCreateQuote({ quote_id: id, trandate: quoteDate, salesrep_id: salesRep || null,
        update_quote_id: nsTarget !== 'new' ? nsTarget : null });
      const oppNum = (res.opportunity && res.opportunity.number) || opportunity.trim();
      setNsQuote({ quote_number: res.quote_number, url: res.url, opportunity: oppNum });
      toast(res.updated
        ? 'NetSuite quote ' + (res.quote_number || res.quote_id) + ' updated — lines synced'
        : 'NetSuite quote ' + (res.quote_number || res.quote_id) + ' created — attached to opportunity ' + oppNum);
      if (res.creator_warning) toast(res.creator_warning, true);
    } catch (e) {
      const connectMsg = nsConnectMessage(e);
      setNsError({ detail: connectMsg || e.response?.data?.detail || e.message, items: e.response?.data?.item_errors || [] });
      toast(connectMsg || 'NetSuite could not save the quote — see the item details in the error panel.', true);
    }
    setCreatingNs(false);
  }

  async function loadQuote() {
    const ref = loadRef.trim().toLowerCase();
    if (!ref) return;
    // Quote numbers (QTAU…/QTUS…) pull straight from NetSuite.
    if (/^qt/i.test(ref)) {
      try {
        const nsq = await api.nsQuoteByNumber(loadRef.trim());
        await hydrateFromNs(nsq);
        toast('Pulled ' + nsq.tranid + ' from NetSuite — ' + (nsq.lines || []).length + ' line(s)');
      } catch (e) { toast(e.response?.data?.detail || 'NetSuite quote lookup failed', true); }
      return;
    }
    try {
      const { items } = await api.listQuotes();
      const q = (items || []).find(x => x.opportunity.toLowerCase() === ref) ||
                (items || []).find(x => x.opportunity.toLowerCase().includes(ref) || x.id === loadRef.trim());
      if (!q) { toast('No saved quote found for "' + loadRef.trim() + '" — for a NetSuite quote, enter its QT number', true); return; }
      hydrate(q);
      toast('Loaded quote ' + q.opportunity);
    } catch (e) { toast('Load failed: ' + (e.response?.data?.detail || e.message), true); }
  }

  // Hydrate the builder from a NetSuite quote: lines land at their NS rate
  // (custom price) and the quote is pre-selected as the update target.
  // Generic Budgetary lines restore the custom display name + edited description.
  async function hydrateFromNs(nsq) {
    dsRequests.current = {}; setEditingItem(null); setExpandedDesc({});
    const lines = nsq.lines || [];
    const codeOf = l => (l.is_generic && l.display_name) ? l.display_name : (l.item || '');
    setQuoteId(null); setNsOpen(null);
    setNsQuote({ quote_number: nsq.tranid, url: nsq.url, opportunity: nsq.opportunity });
    const newRows = [];
    const built = [];
    const toCheck = [];
    lines.forEach(l => {
      // NetSuite Description lines come back on the built-in item and carry
      // only their memo — restore them as text-only lines.
      if (l.is_description) { built.push(newNote({ text: l.memo || '' })); return; }
      const i = newRows.length;
      newRows.push({ ...newRow(), desc: codeOf(l) });
      built.push(newLine(i, {
        code: codeOf(l), displayName: l.display_name || l.item || '', desc: l.memo || '',
        lineRef: l.line_ref || '', notes: l.notes || '',
        qty: l.qty || 1, tier: 'custom', custom: l.rate != null ? String(l.rate) : '',
        nsItemId: l.is_generic ? null : (l.item_id || null), nsFound: !!l.item_id && !l.is_generic,
      }));
      toCheck.push([i, l]);
    });
    setRows(newRows.length ? newRows : [newRow()]);
    setQuoteLines(built);
    setOpportunity(nsq.opportunity || '');
    if (nsq.opportunity) await checkOpportunity(nsq.opportunity);
    setNsTarget(String(nsq.id));
    toCheck.forEach(([i, l]) => checkDatasheet(i, codeOf(l), l.display_name || '', l.is_generic ? null : l.item_id));
  }

  function clearQuote() {
    setAddStatus(''); setNsHits(null);
    dsRequests.current = {}; setEditingItem(null); setExpandedDesc({});
    setQuoteId(null); setOpportunity(''); setQuoteLines([]); setRows([newRow()]); setLoadRef('');
    setOppInfo({ status: 'idle' }); setNsQuote(null); setSalesRep('');
  }

  // Search NetSuite from a pricing row and pull the vendor purchase price into Cost.
  async function findNsPrice(i) {
    const q = (rows[i].desc || '').trim();
    if (!q) { toast('Type an item code in Description first', true); return; }
    const btn = document.querySelector('[data-testid="pc-find-' + i + '"]');
    const rect = btn ? btn.getBoundingClientRect() : { left: 80, bottom: 200 };
    setFindState({ idx: i, hits: null, busy: true, rect });
    try {
      const { items } = await api.nsSearch(q);
      const hits = items || [];
      const exact = hits.find(h => (h.itemid || '').toLowerCase() === q.toLowerCase());
      if (exact || hits.length === 1) { await pullNsPrice(i, exact || hits[0]); return; }
      if (!hits.length) { toast('No NetSuite item found for "' + q + '"', true); setFindState(null); return; }
      setFindState({ idx: i, hits, busy: false, rect });
    } catch (e) { toast('NetSuite search failed: ' + (e.response?.data?.detail || e.message), true); setFindState(null); }
  }
  async function pullNsPrice(i, hit) {
    setFindState({ idx: i, hits: null, busy: true });
    try {
      const info = await api.nsItemFields(hit.id);
      const pp = info.vendor && info.vendor.purchase_price;
      const vendor = (info.vendor && info.vendor.vendor_name) || '';
      const sup = matchSupplier(vendor);
      const supPatch = { nsVendor: vendor, ...(sup ? { supplier: sup } : {}) };
      if (pp == null || pp === '') {
        update(i, { desc: hit.itemid, ...supPatch });
        toast('"' + hit.itemid + '" has no vendor purchase price in NetSuite', true);
      } else {
        update(i, { desc: hit.itemid, cost: String(pp), ...supPatch });
        toast('Pulled cost ' + pp + ' from NetSuite' + (vendor ? ' · supplier ' + (sup || vendor) : ''));
      }
      if (vendor && !sup) toast('NetSuite vendor "' + vendor + '" is not in the supplier list — pick the supplier manually.', true);
    } catch (e) { toast('Could not load item pricing: ' + (e.response?.data?.detail || e.message), true); }
    setFindState(null);
  }

  return (
    <div className="pc-view" data-testid="pricing-calc">
      <div className="pc-head">
        <div>
          <h2>Quote Builder</h2>
          <p className="pc-sub">Type the supplier cost, pick origin currency, supplier and freight — every price tier calculates automatically. EU trade-catalogue suppliers auto-apply their discount.</p>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
          <label className="pc-cur pc-zoom-ctl">Table size <span className="pc-zoom-val">{zoom}%</span>
            <input type="range" data-testid="pc-zoom" min="60" max="120" step="5" value={zoom}
              onChange={e => setZoom(Number(e.target.value))} />
          </label>
          <label className="pc-cur">Display currency
            <select className="input" data-testid="pc-display-currency" value={displayCur} onChange={e => setDisplayCur(e.target.value)}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>
      </div>

      <div className="pc-table-wrap" style={{ '--pricing-zoom': zoom / 100 }}>
        <table className="pc-table">
          <thead>
            <tr>
              <th className="pc-qcol">Quote</th>
              <th>Description</th><th>Cost (origin)</th><th>Cur.</th><th>Supplier</th><th>Freight</th>
              <th>Net cost</th><th>Freight %</th><th>Landed ({displayCur})</th>
              {MARKUP_TIERS.map(t => <th key={t.key} className="tier">{t.label}</th>)}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const res = calcRow(r);
              const sf = SUPPLIER_FREIGHT[r.supplier] || DEFAULT_FREIGHT;
              return (
                <tr key={i} data-testid={'pc-row-' + i}>
                  <td className="pc-qcol"><input type="checkbox" data-testid={'pc-quote-check-' + i} checked={inQuote(i)} onChange={() => toggleQuote(i)} title="Add to quote" /></td>
                  <td className="pc-desc-cell">
                    <div className="pc-desc-wrap">
                      <input className="input" data-testid={'pc-desc-' + i} placeholder="Item / scenario" value={r.desc}
                        onChange={e => update(i, { desc: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') findNsPrice(i); }} />
                      <button className="btn btn-sm pc-find-btn" data-testid={'pc-find-' + i} title="Search NetSuite & pull the vendor cost"
                        disabled={findState && findState.busy} onClick={() => findNsPrice(i)}>
                        {findState && findState.idx === i && findState.busy ? '…' : 'Find'}
                      </button>
                    </div>
                  </td>
                  <td><input className="input pc-cost" data-testid={'pc-cost-' + i} type="number" step="0.01" value={r.cost} onChange={e => update(i, { cost: e.target.value })} />
                    {quoteLines.some(l => l.rowIdx === i && l.costLoading) && <span className="field-hint" role="status" data-testid={'quote-cost-loading-' + i}>Loading cost…</span>}</td>
                  <td>
                    <select className="input" data-testid={'pc-cur-' + i} value={r.originCur} onChange={e => update(i, { originCur: e.target.value })}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="input" data-testid={'pc-supplier-' + i} value={r.supplier} onChange={e => update(i, { supplier: e.target.value })}
                      title={r.nsVendor ? 'NetSuite vendor on this item: ' + r.nsVendor : 'Supplier sets freight % and any EU trade discount'}>
                      {SUPPLIERS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="input" data-testid={'pc-freight-' + i} value={r.freightType} onChange={e => update(i, { freightType: e.target.value })}>
                      <option value="sea">Sea ({Math.round((sf.sea ?? DEFAULT_FREIGHT.sea) * 100)}%)</option>
                      <option value="air">Air ({Math.round((sf.air ?? DEFAULT_FREIGHT.air) * 100)}%)</option>
                      <option value="custom">Custom %</option>
                    </select>
                    {r.freightType === 'custom' && (
                      <input className="input pc-custom-freight" data-testid={'pc-custom-freight-' + i} type="number" step="1" min="0" placeholder="%"
                        value={r.customFreight} onChange={e => update(i, { customFreight: e.target.value })} />
                    )}
                  </td>
                  <td className="num">{res ? res.net.toFixed(2) + ' ' + r.originCur : '—'}</td>
                  <td className="num">{res ? Math.round(res.freight * 100) + '%' : '—'}</td>
                  <td className="num strong" data-testid={'pc-landed-' + i}>{res ? fmt(res.landedAud) : '—'}</td>
                  {MARKUP_TIERS.map(t => (
                    <td key={t.key} className="num tier" data-testid={'pc-' + t.key + '-' + i}>{res ? fmt(res.tiers[t.key]) : '—'}</td>
                  ))}
                  <td className="pc-rowactions">
                    {canPriceUpdate && <button className="btn btn-sm" data-testid={'pc-ns-btn-' + i} title="Update this item's pricing in NetSuite" disabled={!res} onClick={() => setNsOpen(nsOpen === i ? null : i)}>NS</button>}
                    {rows.length > 1 && <button className="btn btn-sm" data-testid={'pc-remove-row-' + i} onClick={() => removeRow(i)}>✕</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {(() => {
            const results = rows.map(r => calcRow(r)).filter(Boolean);
            if (!results.length) return null;
            const sum = pick => results.reduce((a, res) => a + pick(res), 0);
            return (
              <tfoot>
                <tr className="pc-total-row" data-testid="pc-total-row">
                <td colSpan={8} className="pc-total-label">Total ({results.length} item{results.length > 1 ? 's' : ''})</td>
                  <td className="num strong" data-testid="pc-total-landed">{fmt(sum(res => res.landedAud))}</td>
                  {MARKUP_TIERS.map(t => (
                    <td key={t.key} className="num tier strong" data-testid={'pc-total-' + t.key}>{fmt(sum(res => res.tiers[t.key]))}</td>
                  ))}
                  <td></td>
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </div>
      <div className="pc-add-inline">
        <button className="btn btn-sm" data-testid="pc-add-row-inline" onClick={() => setRows(rs => [...rs, newRow()])}>+ Add row</button>
      </div>
      {nsOpen !== null && rows[nsOpen] && (
        <NsPricePanel row={rows[nsOpen]} result={calcRow(rows[nsOpen])} toast={toast} onClose={() => setNsOpen(null)} />
      )}
      {findState && findState.hits && findState.rect && (
        <>
          <div className="pc-find-backdrop" data-testid="pc-find-backdrop" onClick={() => setFindState(null)} />
          <div className="pc-find-pop" data-testid={'pc-find-pop-' + findState.idx}
            style={{ left: Math.max(8, Math.min(findState.rect.left - 120, window.innerWidth - 340)), top: Math.min(findState.rect.bottom + 6, window.innerHeight - 300) }}>
            <div className="pc-find-pop-head">{findState.hits.length} NetSuite matches — pick one to pull its cost</div>
            {findState.hits.map(h => (
              <button key={h.id} className="nsp-search-hit" data-testid={'pc-find-hit-' + h.id} onClick={() => pullNsPrice(findState.idx, h)}>
                <strong>{h.itemid}</strong><span>{h.displayname}</span>
              </button>
            ))}
            <button className="nsp-search-hit pc-find-cancel" data-testid={'pc-find-cancel-' + findState.idx} onClick={() => setFindState(null)}>Cancel</button>
          </div>
        </>
      )}

      <div className="pc-quote" data-testid="quote-builder">
        <div className="pc-quote-steps" data-testid="quote-steps">
          <span><b>1</b> Enter the opportunity #</span>
          <span><b>2</b> Add items (tick rows above or type a code — NetSuite fills the details)</span>
          <span><b>3</b> Set qty & pricing</span>
          <span><b>4</b> Save quote (creates the project + datasheets)</span>
          <span><b>5</b> Push to NetSuite</span>
        </div>
        <div className="pc-quote-head">
          <strong>Quote Builder{quoteId && <span className="pc-quote-editing"> — editing saved quote</span>}</strong>
          <label className="pc-quote-opp">Opportunity #
            <input className="input" data-testid="quote-opp" placeholder="e.g. OPAU025202" value={opportunity}
              onChange={e => { setOpportunity(e.target.value); setOppInfo({ status: 'idle' }); }}
              onBlur={() => checkOpportunity(opportunity)}
              onKeyDown={e => { if (e.key === 'Enter') checkOpportunity(opportunity); }} />
          </label>
          {oppInfo.status === 'checking' && <span className="pc-ds-pill checking" data-testid="opp-status">Checking NetSuite…</span>}
          {oppInfo.status === 'found' && (
            <span className="pc-ds-pill found" data-testid="opp-status" title={oppInfo.data.title}>
              ✓ {oppInfo.data.number}{oppInfo.data.customer_name ? ' — ' + oppInfo.data.customer_name : ''}{oppInfo.data.title ? ': ' + oppInfo.data.title : ''}
            </span>
          )}
          {oppInfo.status === 'missing' && <span className="pc-ds-pill missing" data-testid="opp-status">✕ Opportunity not found — create it in NetSuite first</span>}
          {oppInfo.status === 'error' && <span className="pc-ds-pill missing" data-testid="opp-status" title={oppInfo.detail}>⚠ {oppInfo.detail}</span>}
          <label className="pc-quote-opp">Load quote / opp #
            <input className="input" data-testid="quote-load-ref" placeholder="OPP-… or QTAU-…" value={loadRef} onChange={e => setLoadRef(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') loadQuote(); }} />
          </label>
          <button className="btn btn-sm" data-testid="quote-load" onClick={loadQuote}>Load</button>
          <div className="pc-quote-headright">
            {(quoteId || quoteLines.length > 0) && <button className="btn btn-sm" data-testid="quote-clear" onClick={clearQuote}>New quote</button>}
            <button className="btn btn-primary" data-testid="quote-save" disabled={saving} onClick={saveQuote}>{saving ? 'Saving…' : (quoteId ? 'Update quote' : 'Save quote')}</button>
          </div>
        </div>

        <div className="pc-nsquote-row" data-testid="ns-quote-row">
          <label className="pc-quote-opp">Quote date
            <input className="input" type="date" data-testid="quote-date" value={quoteDate} onChange={e => setQuoteDate(e.target.value)} />
          </label>
          <label className="pc-quote-opp">Sales rep
            {salesReps && salesReps.length > 0 ? (
              <select className="input" data-testid="quote-salesrep" value={salesRep} onChange={e => setSalesRep(e.target.value)}>
                <option value="">— select —</option>
                {salesReps.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            ) : (
              <span className="pc-nsquote-auto" title={repsErr}>{salesReps === null ? 'loading…' : 'unavailable — needs NetSuite access'}</span>
            )}
          </label>
          <span className="pc-nsquote-auto">Probability: <strong>10%</strong> (auto) · Location: <strong>Melbourne Warehouse</strong> (auto) · Created by: <strong>your account</strong> (memo)</span>
          <div className="pc-quote-headright">
            {nsQuote && (
              <a className="btn btn-sm" data-testid="ns-quote-link" href={nsQuote.url} target="_blank" rel="noreferrer">
                NetSuite quote {nsQuote.quote_number || ''}{nsQuote.opportunity ? ' — attached to ' + nsQuote.opportunity : ''} ↗
              </a>
            )}
            <button className="btn" data-testid="ns-quote-create" disabled={creatingNs || oppInfo.status !== 'found' || !quoteLines.length}
              onClick={() => createInNetSuite()}>
              {creatingNs
                ? (nsTarget !== 'new' ? 'Updating…' : 'Creating in NetSuite…')
                : (nsTarget !== 'new'
                  ? 'Update ' + ((oppQuotes.find(x => x.id === nsTarget) || {}).tranid || 'quote') + ' in NetSuite'
                  : 'Create NEW quote in NetSuite')}
            </button>
          </div>
        </div>

        {oppInfo.status === 'found' && (
          <div className={'pc-nsquote-row' + (oppQuotes.length > 0 ? ' pc-nsq-highlight' : '')} data-testid="opp-quotes-row">
            {oppQuotes.length === 0 ? (
              <span className="pc-nsquote-auto" data-testid="opp-quotes-none">No existing quotes on this opportunity — pushing will create the first one.</span>
            ) : (
              <>
                <span className="pc-nsq-title" data-testid="opp-quotes-title">
                  <strong>{oppQuotes.length} existing quote{oppQuotes.length !== 1 ? 's' : ''}</strong> on this opportunity — what do you want to do?
                </span>
                <div className="pc-nsq-modes">
                  <button className={'pc-mode-btn' + (nsTarget === 'new' ? ' on' : '')} data-testid="opp-mode-new" onClick={() => setNsTarget('new')}>
                    ＋ Create NEW quote
                  </button>
                  {oppQuotes.map(qt => (
                    <div className="pc-nsq-quote" key={qt.id}>
                      <button className={'pc-mode-btn' + (nsTarget === qt.id ? ' on' : '')} data-testid={'opp-mode-' + qt.tranid} onClick={() => setNsTarget(qt.id)}
                        title={'Push will UPDATE ' + qt.tranid + ' instead of creating a new quote'}>
                        ✎ Update {qt.tranid}
                        <span className="pc-mode-sub">{qt.date}{qt.total != null ? ' · ' + qt.total.toLocaleString() + ' ' + (qt.currency || '') : ''}</span>
                      </button>
                      <button className="btn btn-sm" data-testid={'opp-quote-preview-' + qt.id} title={'Preview the lines on ' + qt.tranid}
                        onClick={() => { setPreviewQ({ ...qt, lines: null }); api.nsEstimateLines(qt.id).then(d => setPreviewQ(pv => pv && pv.id === qt.id ? { ...pv, lines: d.items || [] } : pv)).catch(() => setPreviewQ(pv => pv && pv.id === qt.id ? { ...pv, lines: [] } : pv)); }}>
                        👁
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="pc-quote-addrow">
          <input className="input" data-testid="quote-add-code" placeholder="Type an item code — searches NetSuite, auto-fills cost & description…"
            value={addCode} disabled={adding} onChange={e => { setAddCode(e.target.value); setAddStatus(''); }} onKeyDown={e => { if (e.key === 'Enter' && !adding) addByCode(); }} />
          <button className="btn" data-testid="quote-add-btn" disabled={adding || !addCode.trim()} onClick={addByCode}>{adding ? 'Searching…' : '+ Add item'}</button>
          <button className="btn" data-testid="quote-add-note-btn" onClick={addNote}
            title="Add a text-only line — no item, no price. Pushes to NetSuite as a Description line.">
            + Description line
          </button>
          {quoteLines.length > 0 && (
            <button className={'btn' + (compact ? ' btn-primary' : '')} data-testid="quote-compact-toggle" aria-pressed={compact}
              title="Fold descriptions down to one line so a long quote fits on screen"
              onClick={() => setCompact(c => !c)}>{compact ? '▤ Compact view: on' : '▤ Compact view'}</button>
          )}
        </div>
        {addStatus && <div className="quote-add-status" role="status" aria-live="polite" data-testid="quote-add-status">{addStatus}</div>}
        {nsError && <div className="quote-error" role="alert" data-testid="quote-netsuite-error">
          <div className="quote-picker-header"><strong>NetSuite could not save this quote</strong><button className="btn btn-sm" type="button" data-testid="quote-error-dismiss" aria-label="Dismiss error" onClick={() => setNsError(null)}>×</button></div>
          {nsError.items.length ? nsError.items.map((error, i) => <div className="quote-error-item" key={i} data-testid={'quote-error-item-' + i}>
            <strong>{error.line ? `Line ${error.line} — ${error.sku || '(unnamed item)'}` : (error.sku ? `Item ${error.sku}` : 'Quote-level error')}</strong>
            <p>{error.reason} {error.action}</p>
            {!error.line && !error.sku && <p>NetSuite did not identify a specific item.</p>}
            <details data-testid={'quote-error-technical-' + i}><summary data-testid={'quote-error-technical-toggle-' + i}>NetSuite details</summary><div>{error.code}{error.path ? ` · ${error.path}` : ''}</div><div>{error.netsuite_detail}</div></details>
          </div>) : <p className="quote-description-text">{nsError.detail}</p>}
        </div>}
        {nsHits && (
          <div className="nsp-search-results pc-quote-hits">
            <div className="pc-quote-hits-title">Multiple NetSuite matches — pick one, or add as generic:</div>
            {nsHits.map(h => (
              <button key={h.id} className="nsp-search-hit" data-testid={'quote-ns-hit-' + h.id} onClick={() => pickNsItem(h)}>
                <strong>{h.itemid}{h.isinactive === 'T' ? ' · INACTIVE' : ''}</strong><span>{h.displayname}</span>
              </button>
            ))}
            <button className="btn btn-sm" data-testid="quote-add-generic" onClick={() => { addLine({ code: addCode.trim(), displayName: addCode.trim(), desc: '', nsItemId: null, nsFound: false, cost: '' }); setNsHits(null); }}>Add “{addCode.trim()}” as Generic Budgetary Item</button>
          </div>
        )}

        {quoteLines.length === 0 ? (
          <div className="empty-note" data-testid="quote-empty">Add items by code above, or tick the “Quote” checkbox on a pricing row.</div>
        ) : (
          <>
          {(() => {
            const unmatched = quoteLines.filter(l => l.dsChecked && !l.dsId && l.dsSource !== 'archive');
            if (!unmatched.length || dsBannerOff) return null;
            return (
              <div className="quote-ds-banner" data-testid="quote-ds-banner">
                <div className="qdb-head">
                  <strong data-testid="quote-ds-banner-count">{unmatched.length} of {itemLines.length} line{itemLines.length !== 1 ? 's' : ''} have no datasheet</strong>
                  <span>They will be missing from the proposal pack. Attach a library datasheet, or change the item.</span>
                  <button className="btn btn-sm" data-testid="quote-ds-banner-toggle" aria-expanded={dsBannerOpen}
                    title={dsBannerOpen ? 'Hide the affected lines' : 'Show which lines have no datasheet'}
                    onClick={() => setDsBannerOpen(o => !o)}>{dsBannerOpen ? '▴ Hide details' : '▾ Show details (' + unmatched.length + ')'}</button>
                  <button className="btn btn-sm" data-testid="quote-ds-recheck"
                    title="Search the library, archive and NetSuite links again for every unmatched line"
                    onClick={() => { unmatched.forEach(l => { setLine(l.rowIdx, { dsChecked: false }); checkDatasheet(l.rowIdx, l.code, l.displayName, l.nsItemId); }); toast('Re-checking ' + unmatched.length + ' line' + (unmatched.length !== 1 ? 's' : '') + ' for datasheets…'); }}>↻ Re-check all</button>
                  <button className="btn btn-sm" data-testid="quote-ds-banner-dismiss" aria-label="Hide datasheet warnings" onClick={() => setDsBannerOff(true)}>×</button>
                </div>
                <div className="qdb-list" hidden={!dsBannerOpen}>
                  {unmatched.map(l => (
                    <div className="qdb-row" key={l.rowIdx} data-testid={'quote-ds-banner-row-' + l.rowIdx}>
                      <span className="qdb-line">Line {quoteLines.indexOf(l) + 1}</span>
                      <span className="qdb-code" title={l.displayName || ''}>{l.code || '(no code)'}</span>
                      <button className="btn btn-sm" data-testid={'quote-ds-find-' + l.rowIdx} onClick={() => setDsFind({ rowIdx: l.rowIdx, code: l.code || l.displayName || '' })}>Find datasheet</button>
                      <button className="btn btn-sm" data-testid={'quote-ds-change-' + l.rowIdx} onClick={() => setEditingItem(l)}>Change item</button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          <table className={'pc-quote-table pc-builder-table' + (compact ? ' compact' : '')} data-testid="quote-lines-table">
            <thead>
              <tr><th>Item</th><th>Display Name</th><th>Description</th><th>Line Ref</th><th>W/O Notes</th><th>Pricing</th><th className="pc-qty">Qty</th><th className="num">Unit ({displayCur})</th><th className="num">Line total</th><th></th></tr>
            </thead>
            <tbody>
              {quoteLines.map((l, lineIndex) => {
                const unit = unitFor(l);
                const dragCls = dragIdx !== null && dropIdx === lineIndex && dragIdx !== lineIndex ? 'drop-target' : (dragIdx === lineIndex ? 'dragging' : '');
                const dragProps = {
                  onDragOver: e => { if (dragIdx !== null) { e.preventDefault(); setDropIdx(lineIndex); } },
                  onDrop: e => { if (dragIdx === null) return; e.preventDefault(); dropLine(dragIdx, lineIndex); setDragIdx(null); setDropIdx(null); },
                };
                const orderControls = (
                  <div className="quote-line-order">
                    <span className="quote-line-handle" draggable data-testid={'quote-line-number-' + lineKey(l)}
                      title="Drag to move this line, or use the arrows"
                      onDragStart={e => { setDragIdx(lineIndex); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(lineIndex)); }}
                      onDragEnd={() => { setDragIdx(null); setDropIdx(null); }}>
                      ⠿ Line {lineIndex + 1}
                    </span>
                    <button type="button" className="btn btn-sm" title="Move line up" aria-label="Move line up" data-testid={'quote-move-up-' + lineKey(l)} disabled={lineIndex === 0} onClick={() => moveLine(lineIndex, -1)}>↑</button>
                    <button type="button" className="btn btn-sm" title="Move line down" aria-label="Move line down" data-testid={'quote-move-down-' + lineKey(l)} disabled={lineIndex === quoteLines.length - 1} onClick={() => moveLine(lineIndex, 1)}>↓</button>
                    {!isNote(l) && (
                      <button type="button" className="btn btn-sm" title="Copy this line (same item, pricing and quantity)" aria-label="Copy line" data-testid={'quote-duplicate-' + l.rowIdx} onClick={() => duplicateLine(lineIndex)}>⧉</button>
                    )}
                  </div>
                );
                if (isNote(l)) {
                  return (
                    <tr key={l.noteId} data-testid={'quote-note-' + l.noteId} className={'quote-note-row ' + dragCls} {...dragProps}>
                      <td className="pc-quote-itemcell">
                        {orderControls}
                        <div className="pc-ns-pill quote-note-pill" data-testid={'quote-note-badge-' + l.noteId}
                          title="Text only — no item, no quantity, no price. Goes to NetSuite as a Description line.">
                          Description line
                        </div>
                      </td>
                      <td colSpan={7} data-label="Description">
                        <textarea className={'input quote-description-text' + (compact ? ' one-line' : '')}
                          data-testid={'quote-note-text-' + l.noteId}
                          rows={compact ? 1 : Math.min(6, Math.max(2, (l.text || '').split('\n').length))}
                          placeholder="Free text — e.g. a heading, an exclusion or a site note"
                          value={l.text} onChange={e => setNoteText(l.noteId, e.target.value)} />
                      </td>
                      <td className="num" data-label="Line total">—</td>
                      <td><button className="btn btn-sm" title="Remove this description line" data-testid={'quote-note-remove-' + l.noteId}
                        onClick={() => setQuoteLines(ls => ls.filter(x => x.noteId !== l.noteId))}>✕</button></td>
                    </tr>
                  );
                }
                return (
                  <tr key={l.rowIdx} data-testid={'quote-line-' + l.rowIdx} className={dragCls} {...dragProps}>
                    <td className="pc-quote-itemcell">
                      {orderControls}
                      <input className="input pc-quote-code" data-testid={'quote-code-' + l.rowIdx} value={l.code} readOnly={l.nsFound}
                        placeholder="Code" onChange={e => setLine(l.rowIdx, { code: e.target.value, dsChecked: false })}
                        onBlur={() => checkDatasheet(l.rowIdx, l.code, l.displayName, l.nsItemId)} />
                      <div className="quote-line-meta">
                        <div className={'pc-ds-pill ' + (!l.dsChecked ? 'checking' : ((l.dsId || l.dsSource === 'archive') ? 'found' : 'missing'))} data-testid={'quote-ds-' + l.rowIdx}
                          title={l.dsId
                            ? 'Matched datasheet: ' + l.dsName + (l.dsSource === 'netsuite' ? ' (linked to this NetSuite item)' : '')
                            : (l.dsSource === 'archive' ? 'Found in the Archive: ' + l.dsName + ' — no library datasheet' : 'No matching datasheet in the library, archive or NetSuite links')}>
                          {!l.dsChecked ? 'Checking…' : (l.dsId
                            ? (l.dsSource === 'netsuite' ? '✓ Datasheet (NetSuite-linked)' : '✓ Datasheet found')
                            : (l.dsSource === 'archive' ? '✓ Archive datasheet' : '✕ No datasheet'))}
                        </div>
                        {l.dsChecked && l.dsDup && (
                          <button className="pc-ds-pill dup" data-testid={'quote-ds-dup-' + l.rowIdx}
                            style={{ cursor: 'pointer' }}
                            title="Multiple datasheets match this code — click to compare and choose which one goes to the proposal pack"
                            onClick={() => setDsPick({ rowIdx: l.rowIdx, matches: (l.dsMatches || []).filter(m => m.source !== 'archive') })}>
                            ⚠ {(l.dsMatches || []).filter(m => m.source !== 'archive').length} matches — choose
                          </button>
                        )}
                        <button type="button" className="btn btn-sm" data-testid={'quote-change-item-' + l.rowIdx} onClick={() => setEditingItem(l)}>Change item</button>
                        <div className={'pc-ns-pill' + (l.nsFound ? ' found' : '')} data-testid={'quote-item-status-' + l.rowIdx}
                          title={l.nsFound ? 'Matched to a NetSuite item record' : 'Not in NetSuite — quoted as Generic Budgetary Item'}>
                          {l.nsFound ? 'NetSuite item' : 'Generic budgetary item'}
                        </div>
                        {l.nsInactive && <div className="pc-ns-pill pc-ns-inactive" data-testid={'pc-ns-inactive-' + l.rowIdx} title="NetSuite will reject the quote while this item is inactive">⚠ Inactive in NetSuite</div>}
                      </div>
                    </td>
                    <td data-label="Display name"><input className="input" data-testid={'quote-display-' + l.rowIdx} placeholder="Display name" value={l.displayName}
                      onChange={e => setLine(l.rowIdx, { displayName: e.target.value })}
                      onBlur={() => checkDatasheet(l.rowIdx, l.code, l.displayName, l.nsItemId)} /></td>
                    <td data-label="Description" className="quote-description-cell">
                      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                        <textarea className={'input quote-description-text' + (compact && !expandedDesc[l.rowIdx] ? ' one-line' : '')}
                          key={'desc-' + (compact ? 'c' : 'f') + (expandedDesc[l.rowIdx] ? 'x' : '')}
                          data-testid={(expandedDesc[l.rowIdx] ? 'quote-desc-full-' : 'quote-desc-') + l.rowIdx}
                          title={compact && !expandedDesc[l.rowIdx] ? (l.desc || '') : undefined}
                          rows={expandedDesc[l.rowIdx] ? Math.min(18, Math.max(8, (l.desc || '').split('\n').length))
                            : (compact ? 1 : Math.min(6, Math.max(3, (l.desc || '').split('\n').length)))}
                          placeholder={l.nsFound ? 'Sales description (from NetSuite)' : 'Description'} value={l.desc}
                          onChange={e => setLine(l.rowIdx, { desc: e.target.value })} />
                        <button className="btn btn-sm" data-testid={'quote-desc-expand-' + l.rowIdx}
                          title={expandedDesc[l.rowIdx] ? 'Collapse description' : 'Expand to read the full description'}
                          onClick={() => setExpandedDesc(m => ({ ...m, [l.rowIdx]: !m[l.rowIdx] }))}>
                          {expandedDesc[l.rowIdx] ? '▴' : '⤢'}
                        </button>
                      </div>
                    </td>
                    <td data-label="Line ref"><input className="input pc-quote-ref" data-testid={'quote-ref-' + l.rowIdx} placeholder="e.g. CL-01" value={l.lineRef} onChange={e => setLine(l.rowIdx, { lineRef: e.target.value })} /></td>
                    <td data-label="W/O notes"><input className="input" data-testid={'quote-notes-' + l.rowIdx} placeholder="W/O notes…" value={l.notes} onChange={e => setLine(l.rowIdx, { notes: e.target.value })} /></td>
                    <td data-label="Pricing">
                      <div className="pc-tier-pills">
                        {TIER_CHOICES.map(t => (
                          <button key={t.key} type="button" data-testid={'quote-tier-' + t.key + '-' + l.rowIdx}
                            className={'pc-tier-pill' + (l.tier === t.key ? ' on' : '')}
                            onClick={() => setLine(l.rowIdx, { tier: t.key })}>{t.label}</button>
                        ))}
                        {l.tier === 'custom' && (
                          <input className="input pc-custom-price" data-testid={'quote-custom-' + l.rowIdx} type="number" step="0.01" min="0"
                            placeholder={'Price (' + displayCur + ')'} value={l.custom} onChange={e => setLine(l.rowIdx, { custom: e.target.value })} />
                        )}
                      </div>
                    </td>
                    <td className="pc-qty" data-label="Quantity"><input className="input" data-testid={'quote-qty-' + l.rowIdx} type="number" min="0" step="1" value={l.qty} onChange={e => setLine(l.rowIdx, { qty: e.target.value })} /></td>
                    <td className="num" data-label={'Unit (' + displayCur + ')'} data-testid={'quote-unit-' + l.rowIdx}>{unit ? fmtN(unit) : '—'}</td>
                    <td className="num strong" data-label="Line total" data-testid={'quote-linetotal-' + l.rowIdx}>{fmtN(unit * (Number(l.qty) || 0))}</td>
                    <td><button className="btn btn-sm" title="Remove from quote" data-testid={'quote-remove-' + l.rowIdx} onClick={() => setQuoteLines(ls => ls.filter(x => x.rowIdx !== l.rowIdx))}>✕</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}
        {quoteLines.length > 0 && (
          <div className="quote-totals-bar" data-testid="quote-totals-bar">
            <span data-testid="quote-totals-count">
              <strong>{itemLines.length}</strong> item{itemLines.length !== 1 ? 's' : ''} ·{' '}
              <strong>{itemLines.reduce((s, l) => s + (Number(l.qty) || 0), 0).toLocaleString()}</strong> unit{itemLines.reduce((s, l) => s + (Number(l.qty) || 0), 0) !== 1 ? 's' : ''}
              {noteCount ? ' · ' + noteCount + ' description line' + (noteCount !== 1 ? 's' : '') : ''}
            </span>
            {itemLines.filter(l => !unitFor(l)).length > 0 && (
              <span className="qtb-warn" data-testid="quote-totals-unpriced">{itemLines.filter(l => !unitFor(l)).length} without a price</span>
            )}
            <span className="qtb-total">Quote total ({displayCur}) <strong data-testid="quote-total">{fmtN(quoteTotal)}</strong></span>
          </div>
        )}
      </div>

      {dsFind && (
        <DsFindModal find={dsFind} onClose={() => setDsFind(null)}
          onChoose={m => { setLine(dsFind.rowIdx, { dsId: m.id, dsName: m.name, dsSource: 'library', dsDup: false, dsMatches: [], dsChecked: true }); setDsFind(null); toast('Datasheet "' + m.name + '" attached to this line'); }} />
      )}

      {editingItem && <QuoteItemPicker line={editingItem} onReplace={replaceItem} onClose={() => setEditingItem(null)} />}
      {previewQ && (
        <div className="modal-overlay" data-testid="opp-quote-preview-modal" onClick={() => setPreviewQ(null)}>
          <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>{previewQ.tranid} <span className="ps-muted" style={{ fontSize: 13 }}>{previewQ.date} · {previewQ.status}{previewQ.total != null ? ' · ' + previewQ.total.toLocaleString() + ' ' + (previewQ.currency || '') : ''}</span></h3>
              <button className="modal-close" data-testid="opp-quote-preview-close" onClick={() => setPreviewQ(null)}>×</button>
            </div>
            {previewQ.lines === null ? <div className="empty-note">Loading lines from NetSuite…</div>
              : previewQ.lines.length === 0 ? <div className="empty-note">No item lines on this quote.</div>
              : (
                <table className="pc-quote-table" style={{ marginTop: 10 }}>
                  <thead><tr><th>Item</th><th>Memo</th><th className="num">Qty</th><th className="num">Rate</th><th className="num">Amount</th></tr></thead>
                  <tbody>
                    {previewQ.lines.map((ln, i) => (
                      <tr key={i}><td><strong title={ln.display_name && ln.display_name !== ln.item ? 'NetSuite item: ' + ln.item : undefined}>{ln.display_name || ln.item}</strong></td><td className="quote-description-text" data-testid={'quote-preview-description-' + i}>{ln.memo}</td>
                        <td className="num">{ln.qty != null ? ln.qty : '—'}</td>
                        <td className="num">{ln.rate != null ? ln.rate.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                        <td className="num strong">{ln.amount != null ? ln.amount.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <a className="btn btn-sm" href={previewQ.url} target="_blank" rel="noreferrer">Open in NetSuite ↗</a>
              <button className="btn btn-sm" data-testid="opp-quote-preview-use" onClick={() => { setNsTarget(previewQ.id); setPreviewQ(null); }}>Set as update target</button>
              <button className="btn btn-primary btn-sm" data-testid="opp-quote-preview-edit" disabled={previewQ.lines === null || previewQ.lines.length === 0}
                title="Pull these lines into the builder so you can edit them and push the update"
                onClick={async () => { const pq = previewQ; setPreviewQ(null); await hydrateFromNs({ ...pq, opportunity: opportunity.trim() }); toast('Loaded ' + pq.tranid + ' into the builder — edit the lines, then push to update it'); }}>
                ✎ Edit in Quote Builder
              </button>
            </div>
          </div>
        </div>
      )}

      {dsPick && (
        <DsCompareModal pick={dsPick} onClose={() => setDsPick(null)}
          onChoose={(m) => { setLine(dsPick.rowIdx, { dsId: m.id, dsName: m.name, dsSource: 'library', dsDup: false }); setDsPick(null); toast('Datasheet "' + m.name + '" will be used for the proposal pack'); }} />
      )}

      {quoteDiff && (
        <div className="modal-overlay" data-testid="quote-diff-modal" onClick={() => setQuoteDiff(null)}>
          <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>What will change on {quoteDiff.target.tranid}</h3>
              <button className="modal-close" data-testid="quote-diff-close" onClick={() => setQuoteDiff(null)}>×</button>
            </div>
            {quoteDiff.loading ? <div className="empty-note">Comparing with the current NetSuite quote…</div> : (
              <>
                <p className="field-hint">Updating replaces the lines on {quoteDiff.target.tranid} in NetSuite with the builder's lines below.</p>
                {['added', 'changed', 'removed'].map(kind => (quoteDiff[kind] || []).length > 0 && (
                  <div key={kind} style={{ marginBottom: 12 }}>
                    <h5 style={{ margin: '0 0 6px', color: kind === 'added' ? '#15803d' : kind === 'removed' ? '#dc2626' : '#b45309' }}>
                      {kind === 'added' ? '＋ Added' : kind === 'removed' ? '－ Removed' : '± Changed'} ({quoteDiff[kind].length})
                    </h5>
                    <table className="si-table" data-testid={'quote-diff-' + kind}>
                      <tbody>
                        {quoteDiff[kind].map((r, i) => (
                          <tr key={i}>
                            <td className="si-item-name" title={r.key}>{r.key}</td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                              {kind === 'changed'
                                ? <><div>qty {r.oldQty} → <b>{r.qty}</b> · unit {fmtN(r.oldUnit)} → <b>{fmtN(r.unit)}</b></div><div>{(r.fields || []).join(', ')} changed</div></>
                                : <>qty {r.qty} · unit {fmtN(r.unit)}</>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
                {quoteDiff.reordered && <div data-testid="quote-diff-reordered"><strong>Updated line order</strong><ol>{quoteDiff.order.map((code, i) => <li key={i}>{code}</li>)}</ol></div>}
                {!quoteDiff.reordered && (quoteDiff.added || []).length === 0 && (quoteDiff.removed || []).length === 0 && (quoteDiff.changed || []).length === 0 && (
                  <div className="empty-note" data-testid="quote-diff-none">No line changes — item details, quantities and prices match the current NetSuite quote.</div>
                )}
                {(quoteDiff.same || 0) > 0 && <div className="field-hint">{quoteDiff.same} line(s) unchanged.</div>}
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn" data-testid="quote-diff-cancel" onClick={() => setQuoteDiff(null)}>Cancel</button>
                  <button className="btn btn-primary" data-testid="quote-diff-confirm" disabled={creatingNs} onClick={() => createInNetSuite(true)}>
                    {creatingNs ? 'Updating…' : 'Confirm — update ' + quoteDiff.target.tranid}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="pc-actions">
        <button className="btn" data-testid="pc-add-row" onClick={() => setRows(rs => [...rs, newRow()])}>+ Add row</button>
        <div className="pc-note">Mark-ups: Project /0.55 · Wholesale /0.50 · Trade /0.40 · Retail /0.35 · End User /0.30 — applied to landed AUD, shown in {displayCur}. Rates: 1 AUD = {CURRENCIES.filter(c => c !== 'AUD').map(c => EXCHANGE_RATES[c] + ' ' + c).join(' · ')}.</div>
      </div>
    </div>
  );
}

// Side-by-side comparison of multiple matching datasheets so the user can
// choose which one goes to the proposal pack.
export function DsFindModal({ find, onClose, onChoose }) {
  const [q, setQ] = useState(find.code || '');
  const [hits, setHits] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async (term) => {
    const text = (term === undefined ? q : term).trim();
    if (!text) return;
    setBusy(true);
    try { const { items } = await api.productSearchDs(text, {}, 25); setHits(items || []); }
    catch (e) { setHits([]); }
    setBusy(false);
  };
  useEffect(() => { if (find.code) search(find.code); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="modal-overlay" data-testid="ds-find-modal" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Attach a datasheet</h3>
          <button className="modal-close" data-testid="ds-find-close" onClick={onClose}>×</button>
        </div>
        <p className="field-hint">Search the datasheet library — the one you pick is attached to this quote line and flows into the project / proposal pack.</p>
        <div className="nsp-search-row">
          <input className="input" data-testid="ds-find-search" autoFocus placeholder="Item code, family or name…" value={q}
            onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') search(); }} />
          <button className="btn btn-sm" data-testid="ds-find-search-btn" disabled={busy || !q.trim()} onClick={() => search()}>{busy ? '…' : 'Search'}</button>
        </div>
        {hits && (
          <div className="nsp-search-results" style={{ marginTop: 10, maxHeight: '52vh', overflowY: 'auto' }}>
            {hits.length === 0 && <div className="empty-note" data-testid="ds-find-empty">No datasheets matched. Try a shorter code, or change the item instead.</div>}
            {hits.map(h => (
              <button key={h.id} className="nsp-search-hit" data-testid={'ds-find-hit-' + h.id} onClick={() => onChoose(h)}>
                <strong>{h.name}</strong><span>{[h.code, h.family, h.scope].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function DsCompareModal({ pick, onClose, onChoose }) {
  const region = useRegion();
  const [docs, setDocs] = useState({}); // id -> {product} | {pdfUrl} | {err}
  useEffect(() => {
    let urls = [];
    (pick.matches || []).forEach(m => {
      api.getProduct(m.id).then(async p => {
        if (p.ds_file_id) {
          try {
            const url = await api.getLegacyObjectUrl(p.ds_file_id);
            urls.push(url);
            setDocs(d => ({ ...d, [m.id]: { pdfUrl: url, product: p } }));
          } catch (e) { setDocs(d => ({ ...d, [m.id]: { err: true, product: p } })); }
        } else setDocs(d => ({ ...d, [m.id]: { product: p } }));
      }).catch(() => setDocs(d => ({ ...d, [m.id]: { err: true } })));
    });
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [pick]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="modal-overlay" data-testid="ds-compare-modal" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '94vw', width: '94vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Multiple datasheets match — compare &amp; choose</h3>
          <button className="modal-close" data-testid="ds-compare-close" onClick={onClose}>×</button>
        </div>
        <p className="field-hint">The chosen datasheet is attached to this quote line and flows into the project / proposal pack.</p>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min((pick.matches || []).length, 3)}, 1fr)`, gap: 14, marginTop: 10 }}>
          {(pick.matches || []).map(m => {
            const d = docs[m.id] || {};
            const p = d.product;
            const hasSheet = p && p.data && Object.keys(p.data).length > 2;
            return (
              <div key={m.id} className="dash-card" data-testid={'ds-compare-' + m.id} style={{ padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div><strong>{m.name}</strong><div className="ps-muted" style={{ fontSize: 12 }}>{m.code}{m.prefix ? ' · prefix match' : ''}</div></div>
                  <button className="btn btn-primary btn-sm" data-testid={'ds-choose-' + m.id} onClick={() => onChoose(m)}>Use this</button>
                </div>
                <div style={{ height: '58vh', overflow: 'auto', border: '1px solid var(--border)', background: '#f6f7f9' }}>
                  {!p && !d.err && <div className="empty-note" style={{ border: 'none' }}>Loading…</div>}
                  {d.err && <div className="empty-note" style={{ border: 'none' }}>Preview unavailable.</div>}
                  {d.pdfUrl && <iframe title={m.name} src={d.pdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} />}
                  {p && !d.pdfUrl && hasSheet && (
                    <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%' }}>
                      {region === 'US' ? <USDatasheetPreview sheet={p.data} pageId={'cmp-' + m.id} /> : <DatasheetPreview sheet={p.data} pageId={'cmp-' + m.id} />}
                    </div>
                  )}
                  {p && !d.pdfUrl && !hasSheet && !d.err && <div className="empty-note" style={{ border: 'none' }}>No preview — entry has no datasheet content yet.</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

